import { useMemo } from "react";
import "./MindMappingReportBSeventhSection.css";

const QTag = ({ q, a, impacts }) => (
  <div style={{display:'flex',flexWrap:'wrap',gap:'5px 10px',alignItems:'center',background:'#f0f4ff',border:'1px solid #c7d7fa',borderRadius:'6px',padding:'7px 12px',marginBottom:'12px',fontSize:'12px',lineHeight:1.5}}>
    <span style={{background:'#3b5bdb',color:'#fff',fontWeight:700,padding:'2px 8px',borderRadius:4,fontSize:'10px',whiteSpace:'nowrap'}}>📋 FORM QUESTION</span>
    <span style={{color:'#1e3a8a',fontStyle:'italic'}}>"{q}"</span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#047857',fontWeight:700}}>You answered: <strong>{a}</strong></span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#7c3aed',fontSize:'11px'}}>📊 Impacts: {impacts}</span>
  </div>
);

/* ── Goal ID → name ───────────────────────────────────────────── */
const GOAL_LOOKUP = {
  "1":  "Learning & Knowledge",
  "2":  "Technology & Digital Behavior",
  "3":  "Work & Career Habits",
  "4":  "Money & Finance",
  "5":  "Health & Wellness",
  "6":  "Lifestyle & Daily Routines",
  "7":  "Social & Community Behavior",
  "8":  "Entertainment & Creativity",
  "9":  "Shopping & Consumption",
  "10": "Problem-Solving & Frustrations",
  "11": "Values & Beliefs",
  "12": "Curiosity & Exploration",
};

/* ── Full goal details with startup ideas, funding range, competition ── */
const GOAL_DETAILS = {
  "Learning & Knowledge": {
    domain:      "EdTech & Knowledge Platforms",
    ideas:       "Online micro-credential platform, Sector-specific skill bootcamp, AR/VR simulation training tool",
    funding:     "₹20 L – ₹3 Cr (Seed)",
    competition: "Medium",
    signal:      (sector) => `EdTech aligned with ${sector} skills shortages has strong demand — look for niche certification gaps that existing platforms ignore.`,
  },
  "Technology & Digital Behavior": {
    domain:      "SaaS & Digital Transformation",
    ideas:       "Vertical SaaS tool for the sector, API-first integration platform, No-code workflow builder",
    funding:     "₹50 L – ₹5 Cr (Seed to Pre-A)",
    competition: "High",
    signal:      (sector) => `Digital adoption in ${sector} is accelerating — build tools that remove the top 3 most painful manual workflows in the value chain.`,
  },
  "Work & Career Habits": {
    domain:      "HR Tech & Productivity Tools",
    ideas:       "Freelance marketplace for sector specialists, Skill verification & credentialing SaaS, AI-powered career coach for the sector",
    funding:     "₹30 L – ₹3 Cr (Seed)",
    competition: "Medium",
    signal:      (sector) => `HR challenges are acutely felt in ${sector} — talent sourcing, onboarding, and retention tools have proven B2B demand and SaaS pricing models.`,
  },
  "Money & Finance": {
    domain:      "Fintech & Financial Inclusion",
    ideas:       "Revenue-based financing for sector SMEs, Embedded insurance product, B2B payment infrastructure",
    funding:     "₹1 Cr – ₹10 Cr (Seed to Series A)",
    competition: "High",
    signal:      (sector) => `Finance access gaps in the ${sector} ecosystem create opportunities for lending, invoice factoring, or insurance products targeting underserved segments.`,
  },
  "Health & Wellness": {
    domain:      "HealthTech & Wellness Platforms",
    ideas:       "Occupational health SaaS for sector workers, Mental wellness subscription, Preventive health diagnostics",
    funding:     "₹30 L – ₹4 Cr (Seed)",
    competition: "Medium",
    signal:      (sector) => `Health outcomes linked to ${sector} create targeted wellness opportunities — occupational health and preventive diagnostics attract both B2B and D2C models.`,
  },
  "Lifestyle & Daily Routines": {
    domain:      "Consumer Apps & D2C",
    ideas:       "Subscription box aligned with sector lifestyle, Habit-tracking app for sector professionals, Smart home/IoT product",
    funding:     "₹15 L – ₹2 Cr (Pre-Seed to Seed)",
    competition: "High",
    signal:      (sector) => `Lifestyle brands layered onto ${sector} interests have strong repeat-purchase potential — subscription models and community-first growth are proven paths.`,
  },
  "Social & Community Behavior": {
    domain:      "Community Platforms & Social Tech",
    ideas:       "Professional network for sector communities, Local commerce super-app, Peer-to-peer knowledge exchange",
    funding:     "₹20 L – ₹3 Cr (Seed)",
    competition: "Medium",
    signal:      (sector) => `Community-led networks within the ${sector} vertical compound quickly through word-of-mouth — zero or low CAC is achievable with strong community value propositions.`,
  },
  "Entertainment & Creativity": {
    domain:      "Creator Economy & Media Tech",
    ideas:       "Short-form video platform for sector creators, AI content generation tool, Sector-specific gamification platform",
    funding:     "₹25 L – ₹5 Cr (Seed)",
    competition: "High",
    signal:      (sector) => `Entertainment and content creation within ${sector} is an underexplored niche — creator monetization tools and sector-specific content platforms have strong virality potential.`,
  },
  "Shopping & Consumption": {
    domain:      "E-Commerce & Retail Tech",
    ideas:       "D2C brand in sector category, Discovery and comparison marketplace, Last-mile delivery optimization",
    funding:     "₹30 L – ₹5 Cr (Seed)",
    competition: "High",
    signal:      (sector) => `The purchase journey in ${sector} has significant friction — discovery tools, curated marketplaces, and transparent pricing platforms capture high-intent buyers.`,
  },
  "Problem-Solving & Frustrations": {
    domain:      "Vertical SaaS & Pain-Point Tools",
    ideas:       "Complaint resolution platform for the sector, Regulatory compliance automation tool, Process audit and optimization SaaS",
    funding:     "₹20 L – ₹3 Cr (Seed)",
    competition: "Low–Medium",
    signal:      (sector) => `Pain-point-driven SaaS in ${sector} builds the fastest — identify the top 3 recurring frustrations of the sector's decision-makers and build the minimum viable fix.`,
  },
  "Values & Beliefs": {
    domain:      "Impact Ventures & Conscious Brands",
    ideas:       "ESG reporting SaaS for sector companies, Impact-first D2C brand, Social enterprise solving a sector problem",
    funding:     "₹15 L – ₹3 Cr (Seed, incl. grants)",
    competition: "Low",
    signal:      (sector) => `Mission-driven ventures in ${sector} attract patient capital and impact investors — ESG compliance tools and social enterprise models are gaining institutional support.`,
  },
  "Curiosity & Exploration": {
    domain:      "Deep Tech & Research Ventures",
    ideas:       "AI/ML solution for a sector-specific problem, Sensor-based IoT product, University spin-off or IP-backed startup",
    funding:     "₹1 Cr – ₹20 Cr (DST grants + Seed)",
    competition: "Low",
    signal:      (sector) => `Deep tech curiosity aligned with ${sector} can access DST-NIDHI grants, IIT incubators, and BIRAC funding — the barrier to entry is high but so are the moats.`,
  },
};

const COMP_COLOR = { Low: "#059669", "Low–Medium": "#0891b2", Medium: "#f59e0b", High: "#e74c3c" };

const getSno = (index) => `7 ${String.fromCharCode(65 + index)}`;

const MindMappingReportBSeventhSection = ({ res, toggle }) => {
  const sector = res?.sector ?? "your sector";

  const userGoals = useMemo(() => {
    if (!res?.goals) return [];
    return res.goals.map((id) => {
      const idStr = id.toString();
      // Numeric ID lookup (form stores IDs like "2")
      if (GOAL_LOOKUP[idStr]) return GOAL_LOOKUP[idStr];
      // String name stored with "Goal " prefix → strip it
      const stripped = idStr.replace(/^Goal\s+/i, "").trim();
      // Verify stripped name exists in GOAL_DETAILS, otherwise use as-is
      return GOAL_DETAILS[stripped] ? stripped : (GOAL_DETAILS[idStr] ? idStr : stripped || idStr);
    });
  }, [res?.goals]);

  return (
    <div className="mm-rb-seventh-container">
      <div className={toggle ? "table-adjust" : "mm-rb-seventh-final-table"}>
        <span className="mm-rb-seventh-corner mm-rb-seventh-corner-top-left" />
        <span className="mm-rb-seventh-corner mm-rb-seventh-corner-top-right" />
        <span className="mm-rb-seventh-corner mm-rb-seventh-corner-bottom-left" />
        <span className="mm-rb-seventh-corner mm-rb-seventh-corner-bottom-right" />

        <div className="mm-rb-seventh-module-title">
          <span className="module-prefix">Module 7:</span> Goal × Sector Startup Opportunity Map
          <span className="mm-rb-premium-tag">★ Report A+ Exclusive</span>
        </div>
        <QTag
          q="Which life goals best represent your personal priorities? (Select all that apply)"
          a={userGoals.length > 0 ? `${userGoals.length} goal${userGoals.length > 1 ? 's' : ''}: ${userGoals.slice(0, 3).join(', ')}${userGoals.length > 3 ? ` +${userGoals.length - 3} more` : ''}` : 'No goals selected'}
          impacts="Startup domain selection, sector-goal alignment, investor narrative, and opportunity identification"
        />
        <div className="mm-rb-seventh-table-scroll-wrapper">
          <table className="mm-rb-seventh-inner-table">
            <thead>
              <tr>
                <th className="mm-rb-seventh-col-sno">S.NO</th>
                <th>Startup Domain</th>
                <th>Core Goal</th>
                <th>Est. Seed Funding</th>
                <th>Competition</th>
                <th>Strategic Signal</th>
              </tr>
            </thead>
            <tbody>
              {userGoals.length > 0 ? (
                userGoals.map((goal, index) => {
                  const d = GOAL_DETAILS[goal];
                  const compColor = COMP_COLOR[d?.competition] ?? "#888";
                  return (
                    <tr key={index}>
                      <td className="mm-rb-seventh-col-sno">{getSno(index)}</td>
                      <td><strong>{d?.domain ?? "Cross-Sector Innovation"}</strong></td>
                      <td className="mm-rb-seventh-user-val">{goal}</td>
                      <td className="mm-rb-seventh-funding">{d?.funding ?? "₹15 L – ₹3 Cr"}</td>
                      <td className="mm-rb-seventh-comp">
                        <span className="mm-rb-comp-badge" style={{ backgroundColor: compColor }}>
                          {d?.competition ?? "Medium"}
                        </span>
                      </td>
                      <td className="mm-rb-seventh-highlight">
                        {d?.signal(sector) ?? `Opportunity aligned with ${sector} — explore niche problem-solution fit.`}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="mm-rb-seventh-col-sno">7 A</td>
                  <td colSpan="5" style={{ textAlign: "center", padding: "20px" }}>No goals selected.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MindMappingReportBSeventhSection;
